/**
 * Profil-Tab Kommunikation — "+ Vorlage" (Batch F2)
 *
 * Modal (Drawer): Vorlage aus den Admin-Einstellungen wählen, Vorschau mit
 * für dieses Profil ersetzten Platzhaltern ansehen, an die E-Mail-Adresse
 * des Profils senden. Backend-Vertrag siehe `src/lib/emailTemplates.ts`.
 *
 * Der Button, der dieses Modal öffnet, ist ohne E-Mail-Adresse am Profil
 * deaktiviert (analog "+ Neue Nachricht", siehe KommunikationTab.tsx).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '../../../components/ui';
import { BRAND, getThemeColors } from '../../../lib/theme';
import { emailTemplatesForUser, type EmailTemplate } from '../../../lib/emailTemplates';
import type { V1User } from '../../../lib/v1';

interface Props {
  dark: boolean;
  user: V1User;
  onSent: () => void;
  onClose: () => void;
}

export function EmailVorlageModal({ dark, user, onSent, onClose }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const recipientEmail = user.email ?? '';

  // Vorlagen einmalig laden.
  useEffect(() => {
    let alive = true;
    setLoadingTemplates(true);
    setLoadError(null);
    emailTemplatesForUser.list()
      .then(list => { if (alive) setTemplates(list); })
      .catch(e => { if (alive) setLoadError(e instanceof Error ? e.message : tr('pflege.comm.template.error_load')); })
      .finally(() => { if (alive) setLoadingTemplates(false); });
    return () => { alive = false; };
  }, [tr]);

  // Vorschau nachladen, sobald eine Vorlage gewählt wurde.
  useEffect(() => {
    if (!selectedId || !user.id) { setPreview(null); return; }
    let alive = true;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    emailTemplatesForUser.previewForUser(selectedId, user.id)
      .then(p => { if (alive) setPreview(p); })
      .catch(e => { if (alive) setPreviewError(e instanceof Error ? e.message : tr('pflege.comm.template.preview_error')); })
      .finally(() => { if (alive) setPreviewLoading(false); });
    return () => { alive = false; };
  }, [selectedId, user.id, tr]);

  const handleSend = useCallback(async () => {
    if (!selectedId || !user.id) return;
    setSending(true);
    setSendError(null);
    try {
      await emailTemplatesForUser.sendToUser(selectedId, user.id);
      onSent();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : tr('pflege.comm.template.send_error'));
    } finally {
      setSending(false);
    }
  }, [selectedId, user.id, tr, onSent]);

  const selectStyle: React.CSSProperties = {
    fontSize: 13, padding: '8px 12px', width: '100%', boxSizing: 'border-box',
    border: `1px solid ${t.border}`, borderRadius: 8, background: t.inputBg,
    color: t.text, outline: 'none', fontFamily: 'Outfit,sans-serif',
  };

  return (
    <Drawer open onClose={onClose} width={520} dark={dark}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${t.border}`, flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: t.heading }}>
            {tr('pflege.comm.template.modal_title')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tr('common.action.close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.muted, fontSize: 18, lineHeight: 1, padding: 4 }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: t.muted }}>
            {tr('pflege.comm.recipient_label')}: <span style={{ color: t.heading }}>{recipientEmail}</span>
          </div>

          {/* Vorlagen-Auswahl */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.muted, display: 'block', marginBottom: 5 }}>
              {tr('pflege.comm.template.select_label')}
            </label>
            {loadError ? (
              <div style={{ fontSize: 12.5, color: '#b42318' }}>{loadError}</div>
            ) : loadingTemplates ? (
              <div style={{ fontSize: 12.5, color: t.muted }}>{tr('common.loading_short')}</div>
            ) : templates.length === 0 ? (
              <div style={{ fontSize: 12.5, color: t.muted, fontStyle: 'italic' }}>{tr('pflege.comm.template.no_templates')}</div>
            ) : (
              <select
                value={selectedId ?? ''}
                onChange={e => setSelectedId(e.target.value ? Number(e.target.value) : null)}
                style={selectStyle}
                aria-label={tr('pflege.comm.template.select_label')}
              >
                <option value="">{tr('pflege.comm.template.select_placeholder')}</option>
                {templates.map(tpl => (
                  <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Vorschau */}
          {selectedId && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: t.muted, display: 'block', marginBottom: 5 }}>
                {tr('pflege.comm.template.preview_label')}
              </label>
              {previewError ? (
                <div data-testid="template-preview-error" style={{ fontSize: 12.5, color: '#b42318' }}>{previewError}</div>
              ) : previewLoading ? (
                <div style={{ fontSize: 12.5, color: t.muted }}>{tr('pflege.comm.template.preview_loading')}</div>
              ) : preview ? (
                <div style={{ border: `1px solid ${t.borderSoft}`, borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{
                    padding: '8px 12px', fontSize: 12.5, fontWeight: 600, color: t.heading,
                    background: t.hover, borderBottom: `1px solid ${t.borderSoft}`,
                  }}>
                    {preview.subject || tr('pflege.comm.no_subject')}
                  </div>
                  <iframe
                    title={tr('pflege.comm.template.preview_label')}
                    srcDoc={preview.html}
                    style={{ width: '100%', height: 280, border: 'none', background: '#fff' }}
                  />
                </div>
              ) : null}
            </div>
          )}

          {sendError && <div style={{ fontSize: 11.5, color: '#b42318' }}>{sendError}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${t.border}`, flexShrink: 0, display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={!selectedId || sending || previewLoading || !preview}
            style={{
              flex: 1, fontSize: 13, fontWeight: 600, background: BRAND, color: '#fff', border: 'none',
              borderRadius: 7, padding: '9px 0',
              cursor: (!selectedId || sending || previewLoading || !preview) ? 'not-allowed' : 'pointer',
              opacity: (!selectedId || sending || previewLoading || !preview) ? 0.6 : 1,
            }}
          >{sending ? '…' : tr('pflege.comm.template.send_action')}</button>
          <button
            type="button"
            onClick={onClose}
            style={{ fontSize: 13, padding: '9px 14px', background: 'none', border: `1px solid ${t.border}`, borderRadius: 7, color: t.muted, cursor: 'pointer' }}
          >{tr('common.action.cancel')}</button>
        </div>
      </div>
    </Drawer>
  );
}
